import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

const getStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
      return 'success';

    case 'assigned':
      return 'warning';

    case 'cancelled':
      return 'error';

    case 'open':
      return 'default';

    default:
      return 'primary';
  }
};

export default function ViewSchedulesModal({
  open,
  onClose,
  schedules,
  loading,
  selectedDraft,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '70vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#65C8D0',
          color: '#fff',
          py: 1.5,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Linked Schedule Details
          </Typography>

          {selectedDraft && (
            <Typography variant="body2">
              Job #{selectedDraft?.job_id?.number_str} | Task #{selectedDraft?.task?.number_str}
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={onClose}
          sx={{ color: '#fff' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2,
          maxHeight: '420px',
          overflowY: 'auto',
        }}>
        {loading ? (
          <Box
            sx={{
              py: 8,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : schedules?.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No schedules found.
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              No schedules have been created for this draft yet.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>#</strong>
                </TableCell>

                <TableCell>
                  <strong>Planned Date</strong>
                </TableCell>

                <TableCell>
                  <strong>Cost Item</strong>
                </TableCell>

                <TableCell>
                  <strong>Assigned Members</strong>
                </TableCell>

                <TableCell>
                  <strong>Status</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {schedules.map((schedule, index) => (
                <TableRow key={schedule._id}>
                  <TableCell>{index + 1}</TableCell>

                  <TableCell>
                    {schedule.planned_date
                      ? dayjs(schedule.planned_date.substring(0, 10)).format('MM/DD/YYYY')
                      : '-'}
                  </TableCell>

                  <TableCell>
                    <Stack spacing={0.5}>
                      {(schedule.cost_item || []).map((item, idx) => (
                        <Typography variant="body2" key={idx}>
                          {item}
                        </Typography>
                      ))}
                    </Stack>
                  </TableCell>

                  <TableCell>
                    {schedule.assigned_members?.length ? (
                      <Stack spacing={0.5}>
                        {schedule.assigned_members.map((emp) => (
                          <Typography key={emp._id} variant="body2">
                            {emp.first_name} {emp.last_name}
                          </Typography>
                        ))}
                      </Stack>
                    ) : (
                      '-'
                    )}
                  </TableCell>

                  <TableCell>
                    {(() => {
                      const today = dayjs().startOf('day');

                      let status = (schedule.status || 'open').toLowerCase();

                      if (status === 'open') {
                        if (schedule.planned_date) {
                          const plannedDate = dayjs(
                            schedule.planned_date.substring(0, 10),
                            'YYYY-MM-DD'
                          );

                          if (plannedDate.isBefore(today, 'day')) {
                            status = 'completed';
                          } else if (plannedDate.isSame(today, 'day')) {
                            status = 'in progress';
                          } else if (schedule.assigned_members?.length) {
                            status = 'assigned';
                          }
                        }
                      }

                      return (
                        <Chip
                          size="small"
                          label={
                            status.charAt(0).toUpperCase() + status.slice(1)
                          }
                          color={getStatusColor(status)}
                        />
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          onClick={onClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ViewSchedulesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  schedules: PropTypes.array,
  loading: PropTypes.bool,
  selectedDraft: PropTypes.object,
};

ViewSchedulesModal.defaultProps = {
  schedules: [],
  loading: false,
  selectedDraft: null,
};